import { pushGtmEvent } from '@/src/analytics/gtm'
import { getGtmPageCountry } from '@/src/utils/analytics/gtm/getGtmPageCountry'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { getUserAuthValues } from '@/src/utils/auth'
import { getUserSelectedCurrencyCode } from '@/src/utils/currency/getCurrencyCode'
import { getFormattedDate } from '@/src/utils/date'
import { getPlatformName } from '@/src/utils/deviceType'
import { isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'
import { useLocale, useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'

interface HomepageSearchParams {
    from: string
    to: string
    fromCity?: string
    toCity?: string
    departDate: Date | null
    returnDate: Date | null
    adults: number
    children: number
    infants: number
    flightClass: string
    isRoundTrip: boolean
}

interface MultiCitySegment {
    from: { airportCode: string; city?: string }
    to: { airportCode: string; city?: string }
    date: Date | null
}

const useHomepageGtmEvent = () => {
    const locale = useLocale()
    const t = useTranslations('metadata')
    const pathname = usePathname()
    const isSignedIn = isUserSignedIn()
    const { email } = getUserAuthValues() || {}
    const userEmailPhone = isSignedIn ? `{"user_email":"${email || 'not_informed'}","user_phone":"not_informed"}` : null
    const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
    const isFlightsPage = pathname?.includes('/flights') || false
    const deviceType = getDeviceType()
    const platform = getPlatformName(deviceType)

    const pushPageViewEvent = (payload = {}) => {
        const eventName = isFlightsPage ? 'page_view_flight_homepage' : 'page_view_homepage'

        const finalPayload = {
            event: eventName,
            page_category: 'home',
            page_country: getGtmPageCountry(hostname),
            page_currency: getUserSelectedCurrencyCode()?.toLowerCase() || undefined,
            page_domain: hostname,
            page_language: locale,
            page_path: typeof window !== 'undefined' ? window.location.pathname : '',
            page_referrer: typeof window !== 'undefined' ? document.referrer || 'no_page_referrer' : 'no_page_referrer',
            page_sub_category: 'no_sub_category',
            page_title: t('title'),
            page_location: typeof window !== 'undefined' ? window.location.href : '',
            user_email_phone: userEmailPhone,
            user_logged_in: isSignedIn ? 'yes' : 'no',
            is_new_flow: 'yes',
            platform,
            ...payload
        }

        pushGtmEvent(finalPayload)
    }

    const pushSearchButtonClickEvent = (searchParams: HomepageSearchParams, payload = {}) => {
        const {
            from,
            to,
            fromCity,
            toCity,
            departDate,
            returnDate,
            adults,
            children,
            infants,
            flightClass,
            isRoundTrip
        } = searchParams

        const tripType = isRoundTrip ? 'round_trip' : 'one_way_trip'
        const item = `${from.toLowerCase()}-${to.toLowerCase()}-${isRoundTrip ? 'rt' : 'ow'}`
        const departureDate = departDate ? getFormattedDate(departDate, 'YYYY/MM/DD') : ''
        const returnDateFormatted = returnDate
            ? getFormattedDate(returnDate, 'YYYY/MM/DD')
            : 'no_returning_departure_date'

        const finalPayload = {
            page_type: 'FlightHomePage',
            event: 'click_flight_search_button',
            item,
            trip_type: tripType,
            source_airport_code: from.toLowerCase(),
            destination_airport_code: to.toLowerCase(),
            source_airport_city: fromCity || '',
            destination_airport_city: toCity || '',
            travelers_adult_count: adults,
            travelers_children_count: children,
            travelers_infants_count: infants,
            travelers_total_count: adults + children + infants,
            departing_departure_date: departureDate,
            returning_departure_date: returnDateFormatted,
            flight_class: flightClass.toLowerCase(),
            product_category: 'flights',
            page_category: 'home',
            page_country: getGtmPageCountry(hostname),
            page_currency: getUserSelectedCurrencyCode()?.toLowerCase() || '',
            page_domain: hostname,
            page_language: locale,
            page_path: typeof window !== 'undefined' ? window.location.pathname : '',
            page_referrer: typeof window !== 'undefined' ? document.referrer || 'no_page_referrer' : 'no_page_referrer',
            page_sub_category: 'flights',
            page_title: t('title'),
            page_location: typeof window !== 'undefined' ? window.location.href : '',
            user_email_phone: userEmailPhone,
            user_logged_in: isSignedIn ? 'yes' : 'no',
            is_new_flow: 'yes',
            platform,
            ...payload
        }

        pushGtmEvent(finalPayload)
    }

    const pushMultiCitySearchButtonClickEvent = (
        segments: MultiCitySegment[],
        adults: number,
        children: number,
        infants: number,
        flightClass: string,
        payload = {}
    ) => {
        // Multicity: collapse the journey to first-segment origin → last-segment destination,
        // using the first/last segment dates and the total sector count.
        const firstSegment = segments[0]
        const lastSegment = segments[segments.length - 1]
        if (!firstSegment?.from.airportCode || !lastSegment?.to.airportCode) {
            return
        }

        const from = firstSegment.from.airportCode
        const to = lastSegment.to.airportCode
        const fromCity = firstSegment.from.city || ''
        const toCity = lastSegment.to.city || ''
        const departDate = firstSegment.date
        const returnDate = lastSegment.date
        const item = `${from.toLowerCase()}-${to.toLowerCase()}-mc`

        const finalPayload = {
            page_type: 'FlightHomePage',
            event: 'click_flight_search_button',
            item,
            trip_type: 'multi_city_trip',
            source_airport_code: from.toLowerCase(),
            destination_airport_code: to.toLowerCase(),
            source_airport_city: fromCity,
            destination_airport_city: toCity,
            travelers_adult_count: adults,
            travelers_children_count: children,
            travelers_infants_count: infants,
            travelers_total_count: adults + children + infants,
            number_of_sectors: segments.length,
            departing_departure_date: departDate ? getFormattedDate(departDate, 'YYYY/MM/DD') : '',
            returning_departure_date: returnDate
                ? getFormattedDate(returnDate, 'YYYY/MM/DD')
                : 'no_returning_departure_date',
            flight_class: flightClass.toLowerCase(),
            product_category: 'flights',
            page_category: 'home',
            page_country: getGtmPageCountry(hostname),
            page_currency: getUserSelectedCurrencyCode()?.toLowerCase() || '',
            page_domain: hostname,
            page_language: locale,
            page_path: typeof window !== 'undefined' ? window.location.pathname : '',
            page_referrer: typeof window !== 'undefined' ? document.referrer || 'no_page_referrer' : 'no_page_referrer',
            page_sub_category: 'flights',
            page_title: t('title'),
            page_location: typeof window !== 'undefined' ? window.location.href : '',
            user_email_phone: userEmailPhone,
            user_logged_in: isSignedIn ? 'yes' : 'no',
            is_new_flow: 'yes',
            platform,
            ...payload
        }

        pushGtmEvent(finalPayload)
    }

    return {
        pushPageViewEvent,
        pushSearchButtonClickEvent,
        pushMultiCitySearchButtonClickEvent
    }
}

export default useHomepageGtmEvent
export type { HomepageSearchParams }
